import React, { useEffect } from 'react';
import { Button } from '@wix/design-system';
import { EditorType, Origin } from '../../manage-installed-apps-page';
import { canUsePremiumApp, isBundleApp } from '../../..';
import { useBiLogger } from '../../../hooks/use-bi-logger';
import s from './app-card.scss';
import { appMarketClickOnFreeTrialCta, appMarketManageAppsActionRequiredClick, appMarketSpotlightArticle, appMarketUpgradeStartFreeTrialImpressionInTheManageAppsPageSrc24Evid156, manageAppsUpgradeSite, } from '@wix/bi-logger-app-market-data/v2';
import { useTranslation } from '../../../hooks/use-translation';
import { useExperiments } from '@wix/wix-experiments-react';
import { getDiscountAmount, getIsDevSale, getIsDiscountTypeIsPercentage, getIsInSale, getSaleType, } from '../../../selectors/managedApp';
export function UpgradeButton({ app, isEligibleToFreeTrial, msid, origin, isBusinessApp, editorType, siteIsPremium, isActionRequiredApp, getAppInstance, bundleApps, benefitApps, }) {
    const { experiments } = useExperiments();
    const biLogger = useBiLogger();
    const appId = app.appId;
    const t = useTranslation();
    const shouldShowFreeTrialLabel = isEligibleToFreeTrial &&
        experiments.enabled('specs.app-market.EnableFreeTrialFeature');
    const showUpgradeSite = !shouldShowFreeTrialLabel &&
        (isBusinessApp || !canUsePremiumApp(app, siteIsPremium));
    const getButtonText = () => {
        if (experiments.enabled('specs.app-market.MoveBundlesToBenefits') && isBundleApp(app?.appId, bundleApps, benefitApps, experiments)) {
            return 'manage-apps.app-card.redeem';
        }
        else if (showUpgradeSite) {
            return 'manage-apps.app-card.upgrade-site-button';
        }
        else if (shouldShowFreeTrialLabel) {
            return 'manage-apps.app-card.start-free-trial';
        }
        else {
            return 'manage-apps.app-card.upgrade';
        }
    };
    const wixSaleExperiment = experiments.enabled('specs.app-market.Sale');
    const devSaleExperiment = experiments.enabled('specs.app-market.DeveloperSaleBadge');
    const isInSale = getIsInSale(app);
    const discountPercentageValue = getDiscountAmount(app);
    const isDiscountTypeIsPercentage = getIsDiscountTypeIsPercentage(app);
    const saleType = getSaleType(app);
    const isDevSale = getIsDevSale(app);
    const reportSaleProps = ((wixSaleExperiment || devSaleExperiment) && isInSale) ? {
        badge_name: isDevSale ? 'dev_sale' : 'wix_sale',
        sale_name: saleType,
        sale_discount: isDiscountTypeIsPercentage && Number(discountPercentageValue),
        upgrade_origin: 'manage-apps',
    } : {
        upgrade_origin: 'manage-apps',
    };
    const buttonOnClick = () => {
        if (isActionRequiredApp) {
            biLogger
                .report(appMarketManageAppsActionRequiredClick({
                msid,
                app_id: appId,
                market: origin,
                action_cta: 'upgrade_site',
            }))
                .catch((e) => console.log(e));
        }
        biLogger
            .report(manageAppsUpgradeSite({
            appId,
            appInstanceId: app?.installationDetails?.instanceId,
            market: origin === Origin.BIZMGR
                ? editorType === EditorType.ADI
                    ? 'adi_dashboard'
                    : 'dashboard'
                : origin,
            msid,
            ...reportSaleProps,
            upgrade_origin: 'manage-apps',
            cta_type: getButtonText() === 'manage-apps.app-card.redeem' ? 'redeem' : 'upgrade',
        }))
            .catch((e) => console.log(e));
        if (showUpgradeSite) {
            handleUpgradeSiteClick(appId, origin, msid, editorType);
        }
        else {
            if (shouldShowFreeTrialLabel) {
                biLogger.report(appMarketClickOnFreeTrialCta({
                    appId,
                    msid,
                    market: origin,
                    origin: 'manage-apps',
                    ...reportSaleProps,
                }));
            }
            const appInstance = getAppInstance(appId);
            handleUpgradeAppClick(appId, app?.pricingDetails?.externalPricingPageUrl, origin, msid, appInstance, editorType);
        }
    };
    useEffect(() => {
        if (!showUpgradeSite && !isActionRequiredApp) {
            biLogger.report(appMarketUpgradeStartFreeTrialImpressionInTheManageAppsPageSrc24Evid156({
                appId,
                msid,
                market: origin,
                action_cta: shouldShowFreeTrialLabel
                    ? 'start_free_trial'
                    : getButtonText() === 'manage-apps.app-card.redeem' ? 'redeem' : 'upgrade',
                ...reportSaleProps,
            }));
        }
    }, []);
    useEffect(() => {
        if (isActionRequiredApp) {
            biLogger
                .report(appMarketSpotlightArticle({
                action_cta: 'upgrade_site',
                app_id: appId,
                msid,
                market: origin,
                ...reportSaleProps,
            }))
                .catch((e) => console.log(e));
        }
    }, []);
    const shouldSkinBeStandard = experiments.enabled('specs.dcc.manageAppsFreeTrialBlue') && shouldShowFreeTrialLabel;
    return (React.createElement("div", null,
        React.createElement(Button, { size: 'tiny', priority: !canUsePremiumApp(app, siteIsPremium) ? 'primary' : 'secondary', skin: shouldSkinBeStandard ? 'standard' : 'premium', onClick: () => buttonOnClick(), className: s.upgradeButtonAlign, dataHook: 'upgrade-button' }, t(getButtonText()))));
}
function handleUpgradeSiteClick(appId, origin, msid, editorType) {
    const domain = editorType === EditorType.EDITORX
        ? `manage.editorx.com`
        : `premium.wix.com`;
    window.open(`https://${domain}/wix/api/premiumStart?appDefId=${appId}&siteGuid=${msid}&pp_origin=manage_apps_${origin}`, '_blank');
}
function handleUpgradeAppClick(appId, appExternalPricingPageUrl, origin, msid, instance, editorType) {
    if (appExternalPricingPageUrl) {
        window.open(`${appExternalPricingPageUrl}?instance=${instance}`, '_blank');
    }
    else {
        const domain = editorType === EditorType.EDITORX ? `manage.editorx.com` : `www.wix.com`;
        window.open(`https://${domain}/apps/upgrade/${appId}?metaSiteId=${msid}&pp_origin=manage_apps_${origin}`, '_blank');
    }
}
//# sourceMappingURL=upgrade-button.js.map